/**
 * Client-side workbook parsing for the preset import path.
 *
 * The workbook is parsed here, in the browser, and never uploaded. Only the
 * values of the column the admin nominates are sent to the API. This keeps
 * sensitive registers off the server entirely - a deliberate data-safety
 * property, not an implementation convenience. Do not add an upload path.
 */
// Type-only: the runtime module is ~1MB minified, so it is loaded on demand
// inside load() and code-split out of the initial bundle (most users never
// open the import modal).
import type * as ExcelJS from "exceljs";

export interface SheetColumn {
  /** 1-based physical column number (1 = column A). The extraction key -
   *  header TEXT is display-only, because real registers routinely carry
   *  duplicate header names and text lookup would always hit the first. */
  columnNumber: number;
  header: string;
}

export interface SheetSummary {
  name: string;
  /** Nominatable (non-blank-header) columns, in sheet order. */
  columns: SheetColumn[];
  rowCount: number;
}

const HEADER_ROW = 1;

async function load(data: ArrayBuffer): Promise<ExcelJS.Workbook> {
  // exceljs ships as CJS; depending on the bundler interop the exports
  // object is the module itself or sits under `default`. Handle both.
  const mod = (await import("exceljs")) as
    | typeof ExcelJS
    | { default: typeof ExcelJS };
  const Excel = "default" in mod ? mod.default : mod;
  const wb = new Excel.Workbook();
  await wb.xlsx.load(data);
  return wb;
}

/**
 * A cell's displayable text. ExcelJS cell values are a union (string,
 * number, Date, rich text, formula result, hyperlink...); `cell.text` is
 * the library's own canonical string rendering of all of them.
 */
function cellText(cell: ExcelJS.Cell): string {
  return (cell.text ?? "").trim();
}

/** Non-blank header cells with their physical column numbers. Blank header
 *  cells are simply not nominatable; keying by column number means they can
 *  never shift a later column's extraction. */
function columnsOf(sheet: ExcelJS.Worksheet): SheetColumn[] {
  const row = sheet.getRow(HEADER_ROW);
  const out: SheetColumn[] = [];
  row.eachCell({ includeEmpty: false }, (cell, colNumber) => {
    const header = cellText(cell);
    if (header) out.push({ columnNumber: colNumber, header });
  });
  return out;
}

/** Summarise every sheet so the admin can pick one and see its columns. */
export async function parseWorkbook(data: ArrayBuffer): Promise<SheetSummary[]> {
  const wb = await load(data);
  return wb.worksheets.map((sheet) => ({
    name: sheet.name,
    columns: columnsOf(sheet),
    rowCount: Math.max(0, sheet.rowCount - HEADER_ROW),
  }));
}

/** Spreadsheet-style label for a 1-based column number (1 -> A, 27 -> AA). */
export function columnLabel(columnNumber: number): string {
  let n = columnNumber;
  let label = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    label = String.fromCharCode(65 + rem) + label;
    n = Math.floor((n - 1) / 26);
  }
  return label;
}

/**
 * Pull one column's values by its physical column number (as reported in
 * `SheetSummary.columns`). Blank cells are skipped rather than emitted as
 * empty strings.
 */
export async function extractColumn(
  data: ArrayBuffer,
  sheetName: string,
  columnNumber: number
): Promise<string[]> {
  const wb = await load(data);
  const sheet = wb.getWorksheet(sheetName);
  if (!sheet) {
    throw new Error(`Sheet not found: ${sheetName}`);
  }

  const known = columnsOf(sheet).some((c) => c.columnNumber === columnNumber);
  if (!known) {
    throw new Error(`Column not found: ${columnNumber}`);
  }

  const values: string[] = [];
  sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    if (rowNumber === HEADER_ROW) return;
    const value = cellText(row.getCell(columnNumber));
    if (value) values.push(value);
  });
  return values;
}
